import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import { getAllJobs } from '../utils/careers'
import JobApplicationForm from '../components/JobApplicationForm'

const Careers = () => {
  const t = useTranslation()
  const { isLoading, language } = useLanguage()
  const location = useLocation()

  usePageSeo({
    enabled: !isLoading,
    title: t('careers.meta.title'),
    description: t('careers.meta.description'),
    keywords: t('careers.meta.keywords'),
    pathname: location.pathname
  })
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    loadJobs()
  }, [])

  const loadJobs = async () => {
    try {
      setLoading(true)
      const allJobs = await getAllJobs()
      setJobs(allJobs)
    } catch (error) {
      console.error('Error loading jobs:', error)
    } finally {
      setLoading(false)
    }
  }

  // Get unique areas/departments for filtering
  const areas = ['all', ...new Set(jobs.map(job => job.area).filter(Boolean))]

  // Filter jobs by area
  const filteredJobs = filter === 'all' 
    ? jobs 
    : jobs.filter(job => job.area === filter)

  // Show loading state if translations are still loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite">
        <main>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite">
      <main>
        {/* Hero Section */}
        <section className="bg-white py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Video Hero on the left */}
              <div className="order-1 lg:order-1">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                  {/* Video background */}
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      // Hide video if it fails to load
                      e.target.style.display = 'none'
                    }}
                  >
                    <source src="/video/team-hiring.mp4" type="video/mp4" />
                  </video>
                  {/* Overlay for better contrast */}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-darkGreen/20 to-transparent"></div>
                </div>
              </div>

              {/* Content on the right */}
              <div className="order-2 lg:order-2">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-darkGreen mb-6 leading-tight">
                  {t('careers.hero.title')}
                </h1>
                <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed">
                  {t('careers.hero.description')}
                </p>
                
                {/* Work from anywhere highlight */}
                <div className="bg-primary-darkGreen/5 rounded-xl p-6 border-l-4 border-primary-darkGreen">
                  <h2 className="text-xl md:text-2xl font-bold text-primary-darkGreen mb-3">
                    {t('careers.workFromAnywhere.title')}
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    {t('careers.workFromAnywhere.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Open Positions Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('careers.openPositions.title')}
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                {t('careers.openPositions.description')}
              </p>

              {/* Filter and Count */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">
                    {filteredJobs.length} {filteredJobs.length !== 1 ? t('careers.openPositions.count.plural') : t('careers.openPositions.count.singular')}
                  </span>
                </div>
                
                {areas.length > 1 && (
                  <div className="flex items-center gap-4">
                    <label htmlFor="area-filter" className="text-sm font-medium text-gray-700">
                      {t('careers.openPositions.filterByArea')}
                    </label>
                    <select
                      id="area-filter"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-darkGreen focus:border-transparent"
                    >
                      {areas.map(area => (
                        <option key={area} value={area}>
                          {area === 'all' ? t('careers.openPositions.allAreas') : area}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Jobs List */}
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
                      <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
                      <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : filteredJobs.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <p className="text-lg text-gray-600">
                    {t('careers.openPositions.noPositions')}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredJobs.map((job) => (
                    <Link
                      key={job.slug}
                      to={`/careers/${job.slug}`}
                      className="block bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200 p-6"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-primary-darkGreen">
                              {job.title || job.slug}
                            </h3>
                            {job.area && (
                              <span className="px-3 py-1 bg-primary-darkGreen/10 text-primary-darkGreen text-sm font-medium rounded-full">
                                {job.area}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                            {job.location && (
                              <div className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span>{job.location}</span>
                              </div>
                            )}
                            {job.type && (
                              <div className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <span>{job.type}</span>
                              </div>
                            )}
                            {job.experience && (
                              <div className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                <span>{job.experience}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {job.date && (
                            <time className="text-sm text-gray-500">
                              {new Date(job.date).toLocaleDateString(language === 'en' ? 'en-US' : 'pt-BR', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </time>
                          )}
                          <svg className="w-5 h-5 text-primary-darkGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Application Form Section */}
        <section id="careers-form" className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('careers.applicationForm.title')}
              </h2>
              <p className="text-lg text-gray-700">
                {t('careers.applicationForm.description')}
              </p>
            </div>
            <JobApplicationForm jobs={jobs} />
          </div>
        </section>
      </main>
    </div>
  )
}

export default Careers

